import { useAuth0 } from "@auth0/auth0-react";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { Navigate, Route, Routes } from "react-router-dom";

import { DealerOrganizationProperties } from "src/routes/DealerOrganization/DealerOrganizationProperties";
import { AddDealerUser } from "src/routes/AddDealerUser";
import { DealerOrganizationUsers } from "src/routes/DealerOrganization/DealerOrganizationUsers";
import { AuthGate } from "src/common/AuthGate";
import { GlobalErrorBoundary } from "src/common/ErrorBoundary/GlobalErrorBoundary";
import { RoleGate } from "src/common/RoleGate";
import { StaffRole } from "src/common/types";
import { useFlags } from "src/common/useFlags";
import { usePageTracking } from "src/common/usePageTracking";
import { AppLayout } from "src/layout/AppLayout";
import { AddDealer } from "src/routes/AddDealer";
import { DealerOrganization } from "src/routes/DealerOrganization";
import { DealerOrganizationDetails } from "src/routes/DealerOrganization/DealerOrganizationDetails";
import { DealerOrganizationFinancials } from "src/routes/DealerOrganization/DealerOrganizationFinancials";
import { EditDealerOrganizationDetails } from "src/routes/DealerOrganization/EditDealerOrganizationDetails";
import { Dealers } from "src/routes/Dealers";
import { EditStaffUser } from "src/routes/EditStaffUser";
import { Logout } from "src/routes/Logout";
import { CellKits } from "src/routes/ProductUnits/CellKits";
import { CellKit } from "src/routes/ProductUnits/CellKits/CellKit";
import { CellKitAssignmentLogs } from "src/routes/ProductUnits/CellKits/CellKit/CellKitAssignmentLogs";
import { CellKitDetails } from "src/routes/ProductUnits/CellKits/CellKit/CellKitDetails";
import { CellKitAdd } from "src/routes/ProductUnits/CellKits/CellKitAdd";
import { Intercoms } from "src/routes/ProductUnits/Intercoms";
import { IntercomDetails } from "src/routes/ProductUnits/Intercoms/Intercom/IntercomDetails";
import { IntercomEdit } from "src/routes/ProductUnits/Intercoms/Intercom/IntercomEdit";
import { IntercomSupportTools } from "src/routes/ProductUnits/Intercoms/Intercom/IntercomSupportTools";
import { IntercomAdd } from "src/routes/ProductUnits/Intercoms/IntercomAdd";
import { Properties } from "src/routes/Properties";
import { Property } from "src/routes/Property";
import { Staff } from "src/routes/Staff";
import { StaffInvite } from "src/routes/StaffInvite";
import { StaffUserLoader } from "src/routes/StaffUserLoader";
import { EditDealerUser } from "src/routes/EditDealerUser";

function App() {
  usePageTracking();
  const { gen4AdminPortal } = useFlags();
  const { user } = useAuth0();
  const ldClient = useLDClient();

  if (user) {
    ldClient?.identify({
      kind: "user",
      key: user.sub,
    });
  }

  return (
    <GlobalErrorBoundary>
      <Routes>
        <Route element={<AuthGate />}>
          <Route path="/logout" element={<Logout />} />
          <Route element={<StaffUserLoader />}>
            <Route element={<AppLayout />}>
              <Route index element={<Navigate to="/dealers" />} />
              <Route path="/dealers">
                <Route index element={<Dealers />} />
                <Route path="add" element={<AddDealer />} />
                <Route path=":id" element={<DealerOrganization />}>
                  <Route
                    index
                    element={<Navigate to="details" replace={true} />}
                  />
                  <Route
                    path="details"
                    element={<DealerOrganizationDetails />}
                  />
                  <Route
                    path="edit"
                    element={<EditDealerOrganizationDetails />}
                  />
                  <Route element={<RoleGate role={StaffRole.Admin} />}>
                    <Route
                      path="financials"
                      element={<DealerOrganizationFinancials />}
                    />
                    <Route path="users">
                      <Route index element={<DealerOrganizationUsers />} />
                      <Route path="invite" element={<AddDealerUser />} />
                      <Route path=":userId/edit" element={<EditDealerUser />} />
                    </Route>
                  </Route>
                  <Route
                    path="properties"
                    element={<DealerOrganizationProperties />}
                  />
                </Route>
              </Route>
              <Route path="/staff">
                <Route index element={<Staff />} />
                <Route element={<RoleGate role={StaffRole.Admin} />}>
                  <Route path="invite" element={<StaffInvite />} />
                </Route>
                <Route path=":staffUserId">
                  <Route index element={<Navigate to="edit" />} />
                  <Route element={<RoleGate role={StaffRole.Admin} />}>
                    <Route path="edit" element={<EditStaffUser />} />
                  </Route>
                </Route>
              </Route>
              <Route path="/product-units">
                <Route index element={<Navigate to="intercoms" />} />
                <Route path="intercoms">
                  <Route index element={<Intercoms />} />
                  <Route path="add" element={<IntercomAdd />} />
                  <Route path=":id">
                    <Route index element={<Navigate to={"details"} />} />
                    <Route path="details" element={<IntercomDetails />} />
                    <Route path="edit" element={<IntercomEdit />} />
                    {gen4AdminPortal && (
                      <Route
                        path="support"
                        element={<IntercomSupportTools />}
                      />
                    )}
                  </Route>
                </Route>
                <Route path="cell-kits">
                  <Route index element={<CellKits />} />
                  <Route path="add" element={<CellKitAdd />} />
                  <Route path=":id" element={<CellKit />}>
                    <Route index element={<Navigate to={"details"} />} />
                    <Route path="details" element={<CellKitDetails />} />
                    <Route
                      path="assignment-logs"
                      element={<CellKitAssignmentLogs />}
                    />
                  </Route>
                </Route>
              </Route>
              <Route path="/properties">
                <Route index element={<Properties />} />
                <Route path=":propertyOrganizationId">
                  <Route index element={<Property />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </GlobalErrorBoundary>
  );
}

export default App;
